import { supabase } from '../lib/supabase';

export interface OrdemFornecedor {
  id: string;
  numero: string | null;
  titulo: string;
  descricao: string | null;
  status: 'enviada' | 'em_execucao' | 'concluida' | 'cancelada';
  anexo_url: string | null;
  anexo_nome: string | null;
  criado_em: string;
  prefeitura: string | null;
}

export interface OrdemEventoFornecedor {
  id: string;
  tipo: 'criada' | 'email_enviado' | 'reenviado' | 'status_alterado' | 'concluida';
  descricao: string | null;
  criado_em: string;
}

export interface ManifestacaoOrigem {
  numero: string | null;
  titulo: string | null;
  descricao: string | null;
  modulo_codigo: string | null;
}

export interface OrdemMensagemFornecedor {
  id: string;
  autor_tipo: 'prefeitura' | 'fornecedor';
  autor_nome: string | null;
  mensagem: string;
  anexo_url: string | null;
  anexo_nome: string | null;
  criado_em: string;
}

export interface OrdemFornecedorDetalhe extends OrdemFornecedor {
  enviado_em: string | null;
  ultima_notificacao_em: string | null;
  eventos: OrdemEventoFornecedor[];
  manifestacao: ManifestacaoOrigem | null;
}

export const STATUS_OS_LABEL: Record<OrdemFornecedor['status'], string> = {
  enviada: 'Enviada',
  em_execucao: 'Em execução',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

export const fornecedorService = {
  /** Ordens de serviço emitidas para o CNPJ do fornecedor logado (RPC server-side). */
  async minhasOrdens(): Promise<OrdemFornecedor[]> {
    const { data, error } = await supabase.rpc('fornecedor_minhas_ordens');
    if (error) throw error;
    return (data ?? []) as OrdemFornecedor[];
  },

  /** Detalhe de uma OS pelo número (só retorna se for do CNPJ do fornecedor logado). */
  async ordemDetalhe(numero: string): Promise<OrdemFornecedorDetalhe | null> {
    const { data, error } = await supabase.rpc('fornecedor_ordem_detalhe', { p_numero: numero });
    if (error) throw error;
    return (data as OrdemFornecedorDetalhe | null) ?? null;
  },

  /** Mensagens da comunicação (prefeitura ↔ fornecedor) de uma OS do fornecedor. */
  async listarMensagens(numero: string): Promise<OrdemMensagemFornecedor[]> {
    const { data, error } = await supabase.rpc('fornecedor_os_mensagens', { p_numero: numero });
    if (error) throw error;
    return (data ?? []) as OrdemMensagemFornecedor[];
  },

  /** Envia mensagem à prefeitura; notifica no sistema quem criou a OS. */
  async enviarMensagem(numero: string, mensagem: string, anexo?: { url: string; nome: string } | null): Promise<void> {
    const { data, error } = await supabase.rpc('fornecedor_os_enviar_mensagem', {
      p_numero: numero, p_mensagem: mensagem, p_anexo_url: anexo?.url ?? null, p_anexo_nome: anexo?.nome ?? null,
    });
    if (error) throw error;
    const res = data as { ok: boolean; erro?: string };
    if (!res?.ok) throw new Error(res?.erro || 'Falha ao enviar a mensagem.');
  },

  /** Faz upload do anexo da mensagem para o bucket público. */
  async uploadAnexo(arquivo: File): Promise<{ url: string; nome: string }> {
    const ext = arquivo.name.includes('.') ? `.${arquivo.name.split('.').pop()}` : '';
    const safe = arquivo.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 60);
    const path = `fornecedor-os/${crypto.randomUUID()}_${safe}${safe.endsWith(ext) ? '' : ext}`;
    const { error } = await supabase.storage.from('publicos').upload(path, arquivo, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('publicos').getPublicUrl(path);
    return { url: data.publicUrl, nome: arquivo.name };
  },
};
